import { Product, Venue } from "@/graphql/operations";
import useUser from "@/hooks/useUser";
import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { Localization } from "../../i18n/types";
import { VenueContext } from "./VenueContext";
import {
  GET_USER_VENUES_QUERY,
  GetUserVenuesInput,
  GetUserVenuesResponse,
} from "./queries/GetUserVenuesQuery";

export type VenueContextProps = {
  localization?: Localization;
};

export default function VenueProvider({
  children,
}: React.PropsWithChildren<VenueContextProps>) {
  const {
    user: { userId },
  } = useUser();
  const { data, loading, error } = useQuery<
    GetUserVenuesResponse,
    GetUserVenuesInput
  >(GET_USER_VENUES_QUERY, {
    variables: {
      venuePortalUserId: userId ?? "",
    },
    onError(error) {
      console.error(error.message);
    },
    skip: !userId,
  });

  const isCurrentUserData = data?.venuePortalUser.id === userId;
  const venues = useMemo(
    () => (isCurrentUserData ? data?.venuePortalUser.venues ?? [] : []),
    [data, isCurrentUserData],
  );
  const userProducts = useMemo(
    () => (isCurrentUserData ? data?.venuePortalUser.products ?? [] : []),
    [data, isCurrentUserData],
  );

  const venueMap = useMemo(() => generateMap(venues), [venues]);

  const productMap = useMemo(() => {
    const map = new Map<Product["id"], Product & { venues: Venue[] }>();
    userProducts.forEach((product) => {
      map.set(product.id, { ...product, venues: [] });
    });
    venues.forEach((venue) => {
      (venue.products ?? []).forEach((product) => {
        const prevValue = map.get(product.id);
        map.set(product.id, {
          ...prevValue,
          ...product,
          venues: [...(prevValue?.venues ?? []), venue],
        });
      });
    });
    return new Map(
      Array.from(map.entries()).map(([productId, product]) => [
        productId,
        {
          ...product,
          defaultVenue:
            product.venues.length === 1 ? product.venues[0] : undefined,
        },
      ]),
    );
  }, [userProducts, venues]);

  const hasMultipleVenues = useMemo(() => venues.length > 1, [venues]);

  const hasSingleVenue = useMemo(() => venues.length === 1, [venues]);

  const defaultVenue = useMemo(
    () => (hasSingleVenue ? venues[0] : undefined),
    [venues, hasSingleVenue],
  );

  return (
    <VenueContext.Provider
      value={{
        defaultVenue,
        error,
        hasMultipleVenues,
        hasSingleVenue,
        loading: loading || (!!userId && data !== undefined && !isCurrentUserData),
        productMap,
        venueMap,
        venues,
      }}
    >
      {children}
    </VenueContext.Provider>
  );
}

function generateMap<T extends { id: string }>(objects: T[]) {
  return new Map(objects.map((object) => [object.id, object]));
}
